// src/pages/ap-autoforward/ApAutoForwardPage.tsx — Info Hub AP Auto-Forward
// control card. Route /ap-autoforward (SSO admin). Master kill-switch + run
// mode (DRY-RUN / LIVE) for the experimental "AP Invoice Auto-Forward" Azure
// Logic App, plus a compact recent-activity feed. Mirrors SchedulerPage's
// data-fetch (useCallback load + useEffect), admin-gate-after-hooks pattern,
// and TopBar usage. Heather/brass admin tone.

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCurrentUser } from "../../auth";
import { apiFetch, ApiError } from "../../api";
import { C, F } from "../../theme";
import { TopBar, Card, Button, Pill, Field } from "../../components/ui";
import type { PillTone } from "../../components/ui";

type Mode = "dry_run" | "live";

interface ApState {
  enabled: boolean;
  mode: Mode;
  cEnabled: boolean;
  cMode: Mode;
  updatedBy: string | null;
  updatedAt: string | null;
  lastRunAt: string | null;
  lastError: string | null;
  counters: unknown;
}

interface ApItem {
  id: number;
  messageId: string;
  receivedAt: string | null;
  senderEmail: string | null;
  supplier: string | null;
  theirDocNo: string | null;
  gross: number | null;
  status: string;
  attempts: number;
  forwardedAt: string | null;
  confirmedAt: string | null;
  iplicitDocNo: string | null;
  lastCheckAt: string | null;
  dryRun: boolean;
  docType: string | null;
  confidence: number | null;
  createMethod: string;
  extractedSupplier: string | null;
  extractedDocNo: string | null;
}

interface ApWhitelistEntry {
  id: number;
  email: string;
  addedBy: string | null;
  addedAt: string | null;
}

interface ApToday {
  processed: number;
  forwarded: number;
  confirmed: number;
  failed: number;
  review: number;
  skipped: number;
  wouldForward: number;
  api: number;
}

interface ApData {
  state: ApState;
  items: ApItem[];
  whitelist: ApWhitelistEntry[];
  today?: ApToday;
}

// Recent-activity tabs — mirror the /items endpoint. Tab 4 ("skipped") is the
// last 50, no paging; the "Create in iplicit →" button shows on tabs 2 + 4.
type ItemTab = "confirmed" | "unconfirmed" | "direct" | "skipped";
interface ItemsPage {
  tab: string;
  page: number;
  hasMore: boolean;
  items: ApItem[];
}
const AP_TABS: { key: ItemTab; label: string; caption: string }[] = [
  { key: "confirmed", label: "Confirmed", caption: "Forwarded by email and matched in iplicit — last 3 days." },
  { key: "unconfirmed", label: "In Flight", caption: "On the Under Review ledger — forwarded, awaiting confirmation (or failed, about to direct-inject)." },
  { key: "direct", label: "Direct Inject", caption: "Created directly in iplicit (the 3-hour fallback), with the iplicit doc as proof." },
  { key: "skipped", label: "Auto Failed", caption: "Forwarded but could not be confirmed OR auto-created — now in the Auto Failed folder for manual entry." },
];

interface ToggleBody {
  enabled?: boolean;
  mode?: Mode;
  cEnabled?: boolean;
  cMode?: Mode;
}

// Friendly "Today · 14:00" / "Yesterday · 21:30" / "12 May · 07:00" for an ISO
// timestamp. Mirrors the Scheduler's formatRunAt so the surfaces read alike.
function humaniseTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const now = new Date();
  const hhmm = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (diffDays === 0) return `Today · ${hhmm}`;
  if (diffDays === 1) return `Yesterday · ${hhmm}`;
  if (diffDays > 1 && diffDays < 7) return `${diffDays} days ago · ${hhmm}`;
  const dm = d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return `${dm} · ${hhmm}`;
}

function formatGross(gross: number | null): string {
  if (gross === null || gross === undefined) return "—";
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(gross);
}

// A single daily-total figure (big number + small label).
function Stat({ label, n }: { label: string; n: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", minWidth: 64 }}>
      <span style={{ fontFamily: F.body, fontSize: 20, fontWeight: 800, color: C.ink }}>{n}</span>
      <span style={{ fontFamily: F.body, fontSize: 11.5, fontWeight: 700, color: C.inkMute }}>{label}</span>
    </div>
  );
}

// Map a ledger item to a human label + pill tone. Dry-run rows read as "would-…"
// (nothing actually sent); live rows show the real lifecycle incl. CONFIRMED/FAILED.
function statusView(it: ApItem): { label: string; tone: PillTone } {
  if (it.dryRun) {
    if (it.status === "SENT") return { label: "Would forward", tone: "heather" };
    if (it.status === "REVIEW") return { label: "Would review", tone: "heather" };
    if (it.status === "SKIPPED") return { label: "Skipped (dry)", tone: "paper" };
    return { label: it.status, tone: "heather" };
  }
  switch (it.status) {
    case "CONFIRMED": return { label: "Confirmed", tone: "sage" };
    case "SENT": return { label: "Forwarded", tone: "paper" };
    case "FAILED": return { label: "Failed", tone: "signal" };
    case "REVIEW": return { label: "Review", tone: "heather" };
    case "SKIPPED": return { label: "Skipped", tone: "paper" };
    default: return { label: it.status, tone: "paper" };
  }
}

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8, fontFamily: F.body, fontSize: 13.5 }}>
      <span style={{ color: C.inkMute, fontWeight: 700, minWidth: 96 }}>{label}</span>
      <span style={{ color: C.inkSoft, fontWeight: 700 }}>{value}</span>
    </div>
  );
}

export function ApAutoForwardPage() {
  const navigate = useNavigate();
  const { me } = useCurrentUser();

  const [data, setData] = useState<ApData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [cMsg, setCMsg] = useState<string | null>(null);

  // Recent-activity tabs — each backed by the paged /items endpoint.
  const [tab, setTab] = useState<ItemTab>("confirmed");
  const [tabItems, setTabItems] = useState<ApItem[]>([]);
  const [tabPage, setTabPage] = useState(1);
  const [tabHasMore, setTabHasMore] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch<ApData>("/api/ap-autoforward");
      setData(r);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't load AP Auto-Forward.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Load one page of a tab. page 1 replaces; later pages append ("Load more").
  const loadTab = useCallback(async (t: ItemTab, page: number) => {
    setTabLoading(true);
    try {
      const r = await apiFetch<ItemsPage>(`/api/ap-autoforward/items?tab=${t}&page=${page}`);
      setTabItems((prev) => (page === 1 ? r.items : [...prev, ...r.items]));
      setTabHasMore(r.hasMore);
      setTabPage(r.page);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't load activity.");
    } finally {
      setTabLoading(false);
    }
  }, []);

  // Reload page 1 whenever the active tab changes (and on first mount).
  useEffect(() => { void loadTab(tab, 1); }, [tab, loadTab]);

  const toggle = useCallback(async (body: ToggleBody) => {
    setBusy(true);
    try {
      await apiFetch<{ state: ApState }>("/api/ap-autoforward/toggle", {
        method: "POST",
        body: JSON.stringify(body),
      });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't save the change.");
    } finally {
      setBusy(false);
    }
  }, [load]);

  const addWhitelist = useCallback(async (email: string) => {
    const e = email.trim().toLowerCase();
    if (!e) return;
    setBusy(true);
    try {
      await apiFetch<{ whitelist: ApWhitelistEntry[] }>("/api/ap-autoforward/whitelist", {
        method: "POST",
        body: JSON.stringify({ email: e }),
      });
      setNewEmail("");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't add the sender.");
    } finally {
      setBusy(false);
    }
  }, [load]);

  const removeWhitelist = useCallback(async (id: number) => {
    setBusy(true);
    try {
      await apiFetch<{ whitelist: ApWhitelistEntry[] }>(`/api/ap-autoforward/whitelist/${id}`, {
        method: "DELETE",
      });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't remove the sender.");
    } finally {
      setBusy(false);
    }
  }, [load]);

  // Mechanism C: create a draft directly in iplicit for one item. Honours the C
  // flag/mode server-side (dry-run resolves + reports without writing).
  const createIplicit = useCallback(async (messageId: string) => {
    setBusy(true);
    setCMsg(null);
    try {
      const r = await apiFetch<{
        ok: boolean; dryRun: boolean; reason?: string; docNo?: string;
        supplier?: { name: string; matchedTo?: string; status: string; score: number };
        extraction?: { supplier: string | null; theirDocNo: string | null };
      }>("/api/ap-autoforward/iplicit-create", { method: "POST", body: JSON.stringify({ messageId }) });
      const sup = r.supplier
        ? ` — "${r.supplier.name}" → ${r.supplier.matchedTo ?? "(no match)"} [${r.supplier.status} ${r.supplier.score}]`
        : "";
      const vis = r.extraction?.supplier ? ` (vision read: ${r.extraction.supplier})` : "";
      if (r.ok && r.docNo) setCMsg(`Created ${r.docNo} in iplicit${sup}${vis}`);
      else if (r.ok && r.dryRun) setCMsg(`Dry-run OK — would create${sup}${vis}`);
      else setCMsg(`Not created: ${r.reason ?? "unknown"}${sup}${vis}`);
      // Refresh counters + the active tab (the row moves to Direct Inject on success).
      await Promise.all([load(), loadTab(tab, 1)]);
    } catch (e) {
      setCMsg(e instanceof ApiError ? e.message : "Couldn't create in iplicit.");
    } finally {
      setBusy(false);
    }
  }, [load, loadTab, tab]);

  // Admin gate — PIN admins / non-admins never see the tile, but guard direct
  // nav too. Placed AFTER all hooks so the hook order never changes.
  if (me && !me.isSsoAdmin) {
    return (
      <div>
        <TopBar title="AP Auto-Forward" sub="Admin" onBack={() => navigate("/apps")} />
        <div style={{ padding: 24, fontFamily: F.serif, fontStyle: "italic", color: C.inkSoft }}>
          This app is for NYMR Entra admins only.
        </div>
      </div>
    );
  }

  const state = data?.state;
  const whitelist = data?.whitelist ?? [];
  const wlSet = new Set(whitelist.map((w) => w.email.toLowerCase()));
  const today = data?.today;

  return (
    <div style={{ minHeight: "100vh", background: C.paper }}>
      <TopBar title="AP Auto-Forward" sub="Admin" onBack={() => navigate("/apps")} />
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "18px 18px 64px" }}>
        {error && (
          <div role="alert" style={{ padding: 12, borderRadius: 12, background: C.signalBg, border: "1px solid rgba(184,58,43,0.22)", fontFamily: F.body, fontSize: 13, color: C.signalDk, fontWeight: 700, marginBottom: 14 }}>
            {error}
          </div>
        )}

        {loading && !data ? (
          <div style={{ padding: "40px 0", textAlign: "center", fontFamily: F.serif, fontStyle: "italic", color: C.inkSoft, fontSize: 16 }}>
            Loading…
          </div>
        ) : !state ? (
          <div style={{ padding: "40px 0", textAlign: "center", fontFamily: F.serif, fontStyle: "italic", color: C.inkSoft, fontSize: 16 }}>
            No status available.
          </div>
        ) : (
          <Card>
            {/* Status chips */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Pill tone={state.enabled ? "sage" : "paper"}>{state.enabled ? "On" : "Off"}</Pill>
              <Pill tone={state.mode === "live" ? "signal" : "heather"}>
                {state.mode === "live" ? "Live" : "Dry-run"}
              </Pill>
            </div>

            {/* Meta rows */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 14 }}>
              <StatRow label="Last run" value={humaniseTime(state.lastRunAt)} />
              {state.updatedBy && <StatRow label="Updated by" value={state.updatedBy} />}
              {state.lastError && <StatRow label="Last error" value={state.lastError} />}
            </div>

            {/* Today's running totals (accumulate across every run today, from the ledger) */}
            {today && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontFamily: F.body, fontSize: 12, fontWeight: 800, color: C.inkMute, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>Today</div>
                <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
                  <Stat label="Processed" n={today.processed} />
                  {state.mode === "live"
                    ? <Stat label="Forwarded" n={today.forwarded} />
                    : <Stat label="Would forward" n={today.wouldForward} />}
                  <Stat label="Confirmed" n={today.confirmed} />
                  <Stat label="Auto-failed" n={today.failed} />
                  <Stat label="In flight" n={today.review} />
                  <Stat label="Duplicates" n={today.skipped} />
                  <Stat label="Direct inject" n={today.api} />
                </div>
              </div>
            )}

            {/* Controls */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16 }}>
              <Button
                variant={state.enabled ? "danger" : "primary"}
                disabled={busy}
                onClick={() => void toggle({ enabled: !state.enabled })}
              >
                {state.enabled ? "Stop" : "Start"}
              </Button>
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => void toggle({ mode: state.mode === "live" ? "dry_run" : "live" })}
              >
                {state.mode === "live" ? "Switch to dry-run" : "Switch to live"}
              </Button>
            </div>

            <div style={{ marginTop: 12, fontFamily: F.serif, fontStyle: "italic", fontSize: 13, color: C.inkSoft }}>
              Experimental — also gated by the weekday 09:00–16:00 schedule and the Logic App being deployed.
            </div>

            {/* Mechanism C — direct iplicit draft create (independent flag + mode) */}
            <div style={{ marginTop: 20, borderTop: `1px solid ${C.hairlineLt}`, paddingTop: 16 }}>
              <div style={{ fontFamily: F.serif, fontSize: 15, fontWeight: 700, color: C.ink, marginBottom: 6 }}>
                Direct iplicit create (Mechanism C)
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                <Pill tone={state.cEnabled ? "sage" : "paper"}>{state.cEnabled ? "C on" : "C off"}</Pill>
                <Pill tone={state.cMode === "live" ? "signal" : "heather"}>{state.cMode === "live" ? "Live" : "Dry-run"}</Pill>
              </div>
              <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 13, color: C.inkSoft, marginBottom: 12 }}>
                For invoices iplicit's OCR can't read (handwritten / scanned): resolves the supplier and creates the draft
                directly, attaching the source PDF. Draft only — never submitted, never guesses a supplier.
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Button variant={state.cEnabled ? "danger" : "primary"} disabled={busy} onClick={() => void toggle({ cEnabled: !state.cEnabled })}>
                  {state.cEnabled ? "Disable C" : "Enable C"}
                </Button>
                <Button variant="secondary" disabled={busy} onClick={() => void toggle({ cMode: state.cMode === "live" ? "dry_run" : "live" })}>
                  {state.cMode === "live" ? "C: switch to dry-run" : "C: switch to live"}
                </Button>
              </div>
              {cMsg && (
                <div style={{ marginTop: 10, fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.inkSoft, wordBreak: "break-word" }}>
                  {cMsg}
                </div>
              )}
            </div>

            {/* Always-forward senders (allow-list) */}
            <div style={{ marginTop: 20, borderTop: `1px solid ${C.hairlineLt}`, paddingTop: 16 }}>
              <div style={{ fontFamily: F.serif, fontSize: 15, fontWeight: 700, color: C.ink, marginBottom: 4 }}>
                Always-forward senders
              </div>
              <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 13, color: C.inkSoft, marginBottom: 12 }}>
                Attachments from these addresses are forwarded straight to iplicit with no AI check. Use only for trusted suppliers.
              </div>
              {whitelist.length === 0 ? (
                <div style={{ fontFamily: F.serif, fontStyle: "italic", color: C.inkMute, fontSize: 14, marginBottom: 12 }}>
                  No always-forward senders yet.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                  {whitelist.map((w) => (
                    <div key={w.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                      <span style={{ fontFamily: F.mono, fontSize: 13, fontWeight: 700, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {w.email}
                      </span>
                      <Button variant="secondary" disabled={busy} onClick={() => void removeWhitelist(w.id)} style={{ minHeight: 36, padding: "6px 12px", fontSize: 13 }}>
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <Field type="email" value={newEmail} onChange={setNewEmail} ph="supplier@example.com" />
              <Button variant="primary" disabled={busy || !newEmail.trim()} onClick={() => void addWhitelist(newEmail)} style={{ minHeight: 40, padding: "8px 14px", fontSize: 14 }}>
                Add sender
              </Button>
            </div>

            {/* Recent activity — 4 tabs, each backed by the paged /items endpoint */}
            <div style={{ marginTop: 20, borderTop: `1px solid ${C.hairlineLt}`, paddingTop: 16 }}>
              <div style={{ fontFamily: F.serif, fontSize: 15, fontWeight: 700, color: C.ink, marginBottom: 10 }}>
                Recent activity
              </div>

              {/* Tab bar */}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                {AP_TABS.map((tb) => {
                  const active = tb.key === tab;
                  return (
                    <button
                      key={tb.key}
                      type="button"
                      disabled={busy || tabLoading}
                      onClick={() => { if (tb.key !== tab) setTab(tb.key); }}
                      style={{
                        cursor: "pointer",
                        fontFamily: F.body,
                        fontSize: 12.5,
                        fontWeight: 800,
                        padding: "6px 12px",
                        borderRadius: 999,
                        border: `1px solid ${active ? C.brassDk : C.hairlineLt}`,
                        background: active ? C.brassDk : "transparent",
                        color: active ? "#fff" : C.inkSoft,
                      }}
                    >
                      {tb.label}
                    </button>
                  );
                })}
              </div>
              <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 12.5, color: C.inkMute, marginBottom: 12 }}>
                {AP_TABS.find((tb) => tb.key === tab)?.caption}
              </div>

              {tabLoading && tabItems.length === 0 ? (
                <div style={{ fontFamily: F.serif, fontStyle: "italic", color: C.inkMute, fontSize: 14 }}>Loading…</div>
              ) : tabItems.length === 0 ? (
                <div style={{ fontFamily: F.serif, fontStyle: "italic", color: C.inkMute, fontSize: 14 }}>
                  Nothing here.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {tabItems.map((it) => {
                    const canCreate =
                      state.cEnabled && (tab === "unconfirmed" || tab === "skipped") &&
                      !it.iplicitDocNo && it.createMethod !== "api" && it.createMethod !== "creating" && it.status !== "CONFIRMED";
                    const docNo = it.theirDocNo || it.extractedDocNo;
                    return (
                      <div key={it.id} style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, padding: "8px 0", borderBottom: `1px solid ${C.hairlineLt}` }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontFamily: F.body, fontSize: 13.5, fontWeight: 800, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {it.supplier || it.extractedSupplier || it.docType || "Unknown supplier"}
                            {docNo ? <span style={{ color: C.inkMute, fontWeight: 700 }}> · {docNo}</span> : null}
                          </div>
                          <div style={{ fontFamily: F.body, fontSize: 12, color: C.inkMute, fontWeight: 700, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {humaniseTime(it.receivedAt ?? it.forwardedAt)}
                            {it.senderEmail ? <span style={{ fontWeight: 700 }}> · {it.senderEmail}</span> : null}
                          </div>
                          {!it.supplier && it.extractedSupplier ? (
                            <div style={{ fontFamily: F.body, fontSize: 11.5, color: C.inkMute, fontWeight: 700, marginTop: 2 }}>
                              Vision read: {it.extractedSupplier}
                            </div>
                          ) : null}
                          {it.senderEmail && it.status !== "SKIPPED" && !wlSet.has(it.senderEmail.toLowerCase()) ? (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => { if (it.senderEmail) void addWhitelist(it.senderEmail); }}
                              style={{ marginTop: 4, background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: F.body, fontSize: 12, fontWeight: 800, color: C.brassDk, textDecoration: "underline" }}
                            >
                              Always forward from this sender
                            </button>
                          ) : null}
                          {canCreate ? (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => void createIplicit(it.messageId)}
                              style={{ marginTop: 4, marginLeft: 14, background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: F.body, fontSize: 12, fontWeight: 800, color: C.brassDk, textDecoration: "underline" }}
                            >
                              Create in iplicit →
                            </button>
                          ) : null}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                          <span style={{ fontFamily: F.mono, fontSize: 12, fontWeight: 700, color: C.inkSoft }}>{formatGross(it.gross)}</span>
                          <Pill small tone={statusView(it).tone}>{statusView(it).label}</Pill>
                        </div>
                      </div>
                    );
                  })}
                  {tabHasMore ? (
                    <Button variant="secondary" disabled={tabLoading} onClick={() => void loadTab(tab, tabPage + 1)} style={{ minHeight: 36, padding: "6px 12px", fontSize: 13, marginTop: 4 }}>
                      {tabLoading ? "Loading…" : "Load more"}
                    </Button>
                  ) : null}
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
